import { type ColorScale } from '@primeuix/themes';

type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;

const OVER_WHITE: Partial<Record<Shade, number>> = {
  50: 0.1,
  100: 0.3,
  200: 0.4,
  300: 0.5,
  400: 0.6,
  500: 0.7,
  600: 0.8,
  700: 0.9,
  800: 1,
};

const OVER_BLACK: Partial<Record<Shade, number>> = {
  900: 0.9,
  950: 0.8,
};

/** Quantises an opacity to the 8-bit alpha the design tool composites with. */
function eightBit(opacity: number): number {
  return Math.floor(opacity * 255) / 255;
}

/** One channel of the base colour laid over white at the given opacity. */
function overWhite(channel: number, opacity: number): number {
  const alpha = eightBit(opacity);
  return Math.round(channel * alpha + 255 * (1 - alpha));
}

/** One channel of the base colour laid over black at the given opacity. */
function overBlack(channel: number, opacity: number): number {
  return Math.round(channel * eightBit(opacity));
}

function toHex(channels: number[]): string {
  return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Builds the 50-950 scale the way the Mojácar Dream palettes are drawn: the brand colour is the 800
 * (the tone of buttons, links and accents) and every other tone is it over white (50-700) or black
 * (900-950) at a fixed opacity. A scale the backoffice already stored is returned as it is, and a
 * value that is not `#rrggbb` yields undefined.
 */
export function brandPalette(input: string | ColorScale): ColorScale | undefined {
  if (typeof input !== 'string') {
    return input;
  }
  if (!HEX_PATTERN.test(input)) {
    return undefined;
  }
  const color = input.toLowerCase();
  const baseChannels = [1, 3, 5].map(start => parseInt(color.slice(start, start + 2), 16));
  const scale: ColorScale = {};
  for (const [shade, opacity] of Object.entries(OVER_WHITE)) {
    scale[Number(shade) as Shade] = toHex(baseChannels.map(ch => overWhite(ch, opacity)));
  }
  for (const [shade, opacity] of Object.entries(OVER_BLACK)) {
    scale[Number(shade) as Shade] = toHex(baseChannels.map(ch => overBlack(ch, opacity)));
  }
  return scale;
}
